<script setup>
import { computed, h, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import { messageStamp } from 'shared/helpers/timeHelper';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  payments: {
    type: Array,
    required: true,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  totalItems: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['pageChange', 'paymentUpdated']);
const { t } = useI18n();
const store = useStore();
const resolvingPaymentId = ref(null);
const globalConfig = useMapGetter('globalConfig/get');

const tableData = computed(() => {
  return props.payments.map(payment => ({
    id: payment.id,
    clientName: payment.clientName || '---',
    clientId: payment.clientId || payment.client_id,
    createdAt: payment.createdAt,
    createdAtFormatted: messageStamp(payment.createdAt, 'LLL d yyyy, h:mm a'),
    expiresAt: payment.expiresAt || payment.expires_at,
    expiresAtFormatted: payment.expiresAt || payment.expires_at
      ? messageStamp(payment.expiresAt || payment.expires_at, 'LLL d yyyy, h:mm a')
      : null,
    amount: payment.amount,
    note: payment.note || '---',
    paymentStatus: payment.paymentStatus,
    requestStatus: payment.requestStatus,
    stripePaymentUrl: payment.stripePaymentUrl,
  }));
});

const paymentStatusLabels = {
  0: 'Open',
  1: 'Paid',
  2: 'Expired',
  3: 'Unknown',
};

const requestStatusLabels = {
  0: 'Open',
  1: 'Resolved',
  2: 'Unknown',
};

const getPaymentStatusColor = (status) => {
  const colorMap = {
    0: 'bg-yellow-100 text-yellow-800', // Open - yellow
    1: 'bg-green-100 text-green-800',   // Paid - green
    2: 'bg-red-100 text-red-800',       // Expired - red
    3: 'bg-gray-100 text-gray-800',       // Unknown - gray
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

const getRequestStatusColor = (status) => {
  const colorMap = {
    0: 'bg-yellow-100 text-yellow-800', // Open - yellow
    1: 'bg-green-100 text-green-800',   // Resolved - green
    2: 'bg-red-100 text-gray-800',       // Unknown - gray
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

const handleResolve = async (paymentId) => {
  if (!paymentId) {
    useAlert('Invalid payment ID');
    return;
  }
  
  resolvingPaymentId.value = paymentId;
  try {
    await store.dispatch('payments/update', {
      id: paymentId,
      request_status: 1,
    });
    useAlert('Payment resolved successfully');
    emit('paymentUpdated');
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || error.message || 'Failed to resolve payment';
    useAlert(errorMessage);
  } finally {
    resolvingPaymentId.value = null;
  }
};

const handleCopyUrl = async (url) => {
  try {
    await copyTextToClipboard(url);
    useAlert('Payment URL copied to clipboard');
  } catch (error) {
    useAlert('Failed to copy URL to clipboard');
  }
};

const getProvetClientUrl = (clientId) => {
  if (!clientId) return null;
  const provetBaseUrl =
    globalConfig.value.provetBaseUrl || window.globalConfig?.PROVET_BASE_URL;
  if (!provetBaseUrl) return null;
  return `${provetBaseUrl}/client/${clientId}/tabs/`;
};

const handleClientNameClick = (clientId) => {
  const url = getProvetClientUrl(clientId);
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

const columnHelper = createColumnHelper();

const columns = computed(() => [
  columnHelper.accessor('clientName', {
    header: 'Client Name',
    width: 200,
    cell: cellProps => {
      const value = cellProps.getValue();
      const row = cellProps.row.original;
      const clientId = row?.clientId;
      const provetUrl = getProvetClientUrl(clientId);
      console.log(provetUrl);
      if (provetUrl && value && value !== '---') {
        return h(
          'a',
          {
            href: provetUrl,
            target: '_blank',
            rel: 'noopener noreferrer',
            onClick: (e) => {
              e.preventDefault();
              handleClientNameClick(clientId);
            },
            class: 'text-n-slate-12 hover:text-blue-600 hover:underline cursor-pointer',
          },
          value
        );
      }
      
      return h('span', { class: 'text-n-slate-12' }, value || '---');
    },
  }),
  columnHelper.accessor('createdAtFormatted', {
    header: 'Created At',
    width: 200,
    cell: cellProps => {
      const value = cellProps.getValue();
      return h('span', { class: 'text-n-slate-12' }, value?.toString() || '---');
    },
  }),
  columnHelper.accessor('expiresAtFormatted', {
    header: 'Expires At',
    width: 200,
    cell: cellProps => {
      const value = cellProps.getValue();
      return h('span', { class: 'text-n-slate-12' }, value?.toString() || '---');
    },
  }),
  columnHelper.accessor('amount', {
    header: 'Amount',
    width: 120,
    align: 'right',
    cell: cellProps => {
      let value = cellProps.getValue();
      if (typeof value === 'number') {
        value = (value / 100.0).toFixed(2);
      } else {
        value = '---';
      }
      return h('span', { class: 'text-n-slate-12 font-medium text-right block' }, value);
    },
  }),
  columnHelper.accessor('note', {
    header: 'Note',
    width: 300,
    cell: cellProps => {
      const value = cellProps.getValue();
      return h(
        'span',
        {
          class: 'line-clamp-2 break-words max-w-full text-n-slate-12',
          title: value,
        },
        value
      );
    },
  }),
  columnHelper.accessor('paymentStatus', {
    header: 'Payment Status',
    width: 150,
    cell: cellProps => {
      const value = cellProps.getValue();
      const displayValue = paymentStatusLabels[value] || value?.toString() || '---';
      const colorClass = getPaymentStatusColor(value);
      return h('span', { class: `px-2 py-1 rounded-md text-xs font-medium ${colorClass}` }, displayValue);
    },
  }),
  columnHelper.accessor('requestStatus', {
    header: 'Request Status',
    width: 150,
    cell: cellProps => {
      const value = cellProps.getValue();
      const displayValue = requestStatusLabels[value] || value?.toString() || '---';
      const colorClass = getRequestStatusColor(value);
      return h('span', { class: `px-2 py-1 rounded-md text-xs font-medium ${colorClass}` }, displayValue);
    },
  }),
  columnHelper.accessor('stripePaymentUrl', {
    header: 'Payment Link',
    width: 200,
    cell: cellProps => {
      const value = cellProps.getValue();
      if (!value) {
        return h('span', { class: 'text-n-slate-12' }, '---');
      }
      return h(
        'button',
        {
          onClick: () => handleCopyUrl(value),
          class: 'text-black bg-n-slate-3 border border-black rounded-md hover:bg-n-slate-4 underline cursor-pointer',
        },
        'Copy Link'
      );
    },
  }),
  columnHelper.display({
    id: 'resolve',
    header: 'Actions',
    width: 120,
    cell: cellProps => {
      if (!cellProps || !cellProps.row) {
        return h('span', { class: 'text-n-slate-11 text-sm' }, '---');
      }
      
      const row = cellProps.row.original;
      if (!row || row.id === undefined || row.id === null) {
        return h('span', { class: 'text-n-slate-11 text-sm' }, '---');
      }
      
      const requestStatus = row.requestStatus;
      const paymentId = row.id;
      const isResolving = resolvingPaymentId.value === paymentId;
      
      if (requestStatus !== 0 && requestStatus !== '0') {
        return h('span', { class: 'text-n-slate-11 text-sm' }, '---');
      }
      
      return h(
        'button',
        {
          text: isResolving ? 'Resolving...' : 'Resolve',
          onClick: () => {
            if (paymentId) {
              handleResolve(paymentId);
            }
          },
          disabled: isResolving,
          class: 'px-3 py-1 text-sm font-medium text-black bg-n-slate-3 border border-black rounded-md hover:bg-n-slate-4 disabled:opacity-50 disabled:cursor-not-allowed',
        },
        isResolving ? 'Resolving...' : 'Resolve'
      );
    },
  }),
]);

const paginationParams = computed(() => {
  return {
    pageIndex: props.currentPage - 1,
    pageSize: 15,
  };
});

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return props.totalItems;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    emit('pageChange', newPagination.pageIndex + 1);
  },
});
</script>

<template>
  <div
    class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
  >
    <Table :table="table" class="max-h-[calc(100vh-21.875rem)]" />
    <div
      v-show="!tableData.length"
      class="h-48 flex items-center justify-center text-n-slate-12 text-sm"
    >
      No payments found
    </div>
    <div v-if="totalItems" class="table-pagination">
      <Pagination class="mt-2" :table="table" />
    </div>
  </div>
</template>
